import { useCallback } from 'react';

import {
  useChatPrompt,
  type ChatPromptAttachmentConfig,
  type ChatPromptPayload,
  type ChatPromptSenderArgs,
  type ChatPromptSendConfig,
  type UseChatPromptOptions,
} from './chat/index';

export interface GatewayPromptStateConfig {
  enableFileUpload?: boolean;
  attachment?: Partial<ChatPromptAttachmentConfig>;
  send?: Partial<Omit<ChatPromptSendConfig, 'onSend'>>;
  inputOptions?: UseChatPromptOptions['inputOptions'];
  initialPrompt?: string;
  buildPayload?: (args: ChatPromptSenderArgs) => ChatPromptPayload;
  onInvalidFile?: (file: File, reason: string) => void;
}

interface UseGatewayPromptStateArgs {
  onSend: (args: { prompt: string; displayContent: string; file?: File | null }) => Promise<void>;
  enableFileUpload?: boolean;
  config?: GatewayPromptStateConfig;
}

const defaultGatewayPayload = ({
  prompt,
  file,
}: {
  prompt: string;
  file: File | null;
}): ChatPromptPayload => {
  if (file) {
    return {
      prompt: 'Analyze this CSV file',
      displayContent: `📎 Uploaded: ${file.name}`,
    };
  }

  return {
    prompt,
    displayContent: prompt,
  };
};

export const useGatewayPromptState = ({
  onSend,
  enableFileUpload: legacyEnableFileUpload,
  config,
}: UseGatewayPromptStateArgs) => {
  const effectiveEnableFileUpload = config?.enableFileUpload ?? legacyEnableFileUpload ?? true;

  const defaultOnInvalidFile = useCallback((_: File, reason: string) => {
    alert(reason);
  }, []);

  const resolvedOnInvalidFile = config?.onInvalidFile ?? defaultOnInvalidFile;

  const { clearOnPromptChange, ...attachmentOverrides } = config?.attachment ?? {};

  const attachmentConfig: ChatPromptAttachmentConfig = {
    enabled: effectiveEnableFileUpload,
    allowedExtensions: ['csv'],
    onInvalidFile: resolvedOnInvalidFile,
    ...attachmentOverrides,
  };

  if (attachmentOverrides.enabled !== undefined) {
    attachmentConfig.enabled = attachmentOverrides.enabled;
  }

  if (clearOnPromptChange !== undefined) {
    attachmentConfig.clearOnPromptChange = clearOnPromptChange;
  }

  if (!attachmentConfig.allowedExtensions) {
    attachmentConfig.allowedExtensions = ['csv'];
  }

  if (!attachmentConfig.onInvalidFile) {
    attachmentConfig.onInvalidFile = resolvedOnInvalidFile;
  }

  const fallbackBuildPayload = ({ prompt, file }: ChatPromptSenderArgs): ChatPromptPayload =>
    defaultGatewayPayload({ prompt, file });

  const sendOverrides = config?.send ?? {};
  const resolvedBuildPayload =
    sendOverrides.buildPayload ?? config?.buildPayload ?? fallbackBuildPayload;

  const sendConfig: ChatPromptSendConfig = {
    onSend,
    ...sendOverrides,
    buildPayload: resolvedBuildPayload,
  };

  const {
    prompt,
    onPromptChange,
    sendPrompt,
    onFileSelected,
    clearFile,
    attachmentLabel,
    selectedFile,
    isSending,
  } = useChatPrompt({
    initialPrompt: config?.initialPrompt,
    inputOptions: config?.inputOptions,
    sendConfig,
    attachmentConfig,
  });

  return {
    userPrompt: prompt,
    onUserPromptChange: onPromptChange,
    sendPrompt,
    handleFileSelected: onFileSelected,
    clearFileSelection: clearFile,
    selectedFileName: attachmentLabel,
    selectedFile,
    isSending,
  };
};
